import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    primary: {
      main: '#667eea',
      light: '#a5b4fc',
      dark: '#5568d3',
      contrastText: '#fff',
    },
    secondary: {
      main: '#764ba2',
      light: '#9d7ec2',
      dark: '#5a3a7a',
      contrastText: '#fff',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a202c',
      secondary: '#4a5568',
    },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 800,
      letterSpacing: '-0.03em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.5,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.8,
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.7,
      fontWeight: 400,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 1px 3px rgba(0,0,0,0.04), 0px 1px 2px rgba(0,0,0,0.02)',
    '0px 2px 6px rgba(0,0,0,0.05), 0px 1px 3px rgba(0,0,0,0.03)',
    '0px 4px 12px rgba(0,0,0,0.06), 0px 2px 4px rgba(0,0,0,0.03)',
    '0px 6px 18px rgba(0,0,0,0.07), 0px 3px 6px rgba(0,0,0,0.04)',
    '0px 8px 24px rgba(0,0,0,0.08), 0px 4px 8px rgba(0,0,0,0.04)',
    '0px 10px 30px rgba(0,0,0,0.09), 0px 5px 10px rgba(0,0,0,0.05)',
    '0px 12px 36px rgba(0,0,0,0.1), 0px 6px 12px rgba(0,0,0,0.05)',
    '0px 14px 42px rgba(0,0,0,0.11), 0px 7px 14px rgba(0,0,0,0.06)',
    '0px 16px 48px rgba(0,0,0,0.12), 0px 8px 16px rgba(0,0,0,0.06)',
    '0px 18px 54px rgba(0,0,0,0.13), 0px 9px 18px rgba(0,0,0,0.07)',
    '0px 20px 60px rgba(0,0,0,0.14), 0px 10px 20px rgba(0,0,0,0.07)',
    '0px 22px 66px rgba(0,0,0,0.15), 0px 11px 22px rgba(0,0,0,0.08)',
    '0px 24px 72px rgba(0,0,0,0.16), 0px 12px 24px rgba(0,0,0,0.08)',
    '0px 26px 78px rgba(0,0,0,0.17), 0px 13px 26px rgba(0,0,0,0.09)',
    '0px 28px 84px rgba(0,0,0,0.18), 0px 14px 28px rgba(0,0,0,0.09)',
    '0px 30px 90px rgba(0,0,0,0.19), 0px 15px 30px rgba(0,0,0,0.1)',
    '0px 32px 96px rgba(0,0,0,0.2), 0px 16px 32px rgba(0,0,0,0.1)',
    '0px 34px 102px rgba(0,0,0,0.21), 0px 17px 34px rgba(0,0,0,0.11)',
    '0px 36px 108px rgba(0,0,0,0.22), 0px 18px 36px rgba(0,0,0,0.11)',
    '0px 38px 114px rgba(0,0,0,0.23), 0px 19px 38px rgba(0,0,0,0.12)',
    '0px 40px 120px rgba(0,0,0,0.24), 0px 20px 40px rgba(0,0,0,0.12)',
    '0px 42px 126px rgba(0,0,0,0.25), 0px 21px 42px rgba(0,0,0,0.13)',
    '0px 44px 132px rgba(0,0,0,0.26), 0px 22px 44px rgba(0,0,0,0.13)',
    '0px 46px 138px rgba(0,0,0,0.27), 0px 23px 46px rgba(0,0,0,0.14)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '10px 24px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          boxShadow: '0px 4px 12px rgba(102, 126, 234, 0.25)',
          '&:hover': {
            boxShadow: '0px 8px 20px rgba(102, 126, 234, 0.35)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(0,0,0,0.04), 0px 4px 16px rgba(0,0,0,0.06)',
          borderRadius: 12,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0,0,0,0.06), 0px 8px 24px rgba(0,0,0,0.08)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667eea',
              },
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'scale(1.1)',
          },
        },
      },
    },
  },
});
